import React, { useEffect, useState, useRef } from "react";
import { Upload, RefreshCw, Plus, Copy, X, AlertTriangle, ChevronRight, Link as LinkIcon, Pencil, Mail, Sheet as SheetIcon } from "lucide-react";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import AddLeadModal from "@/components/AddLeadModal";
import { api } from "@/lib/api";
import { useIsMobile } from "@/hooks/useIsMobile";

/* ============================================================================
 * Phase 7.0 — Import (Sources & Sync) visual restyle
 * Warm-ivory editorial system @ Catalog R1 / Accounts R2 compact density.
 * VISUAL-ONLY: every data-testid, native <input>, native <button>, native
 * <select>, useState, useEffect, useRef, axios call, modal trigger, file-input
 * ref, and toast call is preserved bit-for-bit from R0.
 * Modals (AddLeadModal, AddCsvUrlModal, PreviewModal) — unchanged structurally
 * except for purely visual class/inline-style swaps inside the two local
 * modals; AddLeadModal is external and untouched.
 * ========================================================================== */

export default function Import() {
  const [sources, setSources] = useState([]);
  const [serviceAccount, setServiceAccount] = useState("");
  const [issues, setIssues] = useState([]);
  const [sheetCfg, setSheetCfg] = useState(null);
  const [csvUrlOpen, setCsvUrlOpen] = useState(false);
  const [addLeadOpen, setAddLeadOpen] = useState(false);
  const [previewModal, setPreviewModal] = useState(null);
  // Module 6J — allow re-editing an already-connected Google Sheet source.
  // The SheetConnectCard is normally hidden when status === "connected".
  // Toggle this state (from the Method-card pencil or the Configured Sources
  // "Edit" action) to force the card back into view pre-filled with the
  // current cfg. Cleared on save/cancel/disconnect.
  const [editingSheet, setEditingSheet] = useState(false);
  const fileRef = useRef(null);
  const isMobile = useIsMobile();

  const refresh = async () => {
    const [s, i, g] = await Promise.all([
      api.get("/import/sources"),
      api.get("/import/issues"),
      api.get("/settings/google-sheet"),
    ]);
    setSources(s.data.items || []);
    setServiceAccount(s.data.platform_service_account || "");
    setIssues(i.data.items || []);
    setSheetCfg(g.data);
  };

  useEffect(() => { refresh(); }, []);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const { data: preview } = await api.post("/import/preview", { source_type: "csv_text", csv_text: text });
      setPreviewModal({ ...preview, source_type: "csv_text", csv_text: text, filename: file.name });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not read file");
    } finally {
      e.target.value = "";
    }
  };

  const syncOne = async (id) => {
    try {
      const { data } = await api.post(`/import/sources/${id}/sync`);
      const c = data.counts;
      toast.success(`Synced: +${c.inserted} · refreshed ${c.refreshed}`);
      refresh();
    } catch (err) {
      const det = err?.response?.data?.detail;
      toast.error(typeof det === "string" ? det : det?.error || "Sync failed");
    }
  };

  const syncAll = async () => {
    try {
      const { data } = await api.post("/sync/now");
      const c = data.counts;
      toast.success(`Sync all: +${c.inserted} · refreshed ${c.refreshed} · skipped ${c.skipped}`);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sync failed");
    }
  };

  const sheetConnected = sheetCfg?.status === "connected";

  return (
    <Layout>
      <div className="fade-up" data-testid="import-page">
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
            flexWrap: "wrap",
            gap: 16,
            marginBottom: 26,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: "#B08D55",
                marginBottom: 8,
              }}
            >
              IMPORT
            </div>
            <h1
              className="font-display import-h1"
              style={{ fontSize: isMobile ? 32 : 44, lineHeight: 1.05, color: "#22382C", marginBottom: 6 }}
              data-testid="import-title"
            >
              Sources &amp; Sync
            </h1>
            <p style={{ fontSize: 13, color: "#76705F", margin: 0 }}>
              5 ways to bring leads into Lead Studio.
            </p>
            {/* Phase 7.1 — mobile visual tweaks (< 1024px):
                  • Method-cards grid → single column (was repeat(3,1fr))
                  • SheetConnectCard 3-col field row → single column
                  • Header actions wrap comfortably (already flex-wrap)
                  • Configured-source rows tighten so meta + button stack */}
            <style>{`
              @media (max-width: 1023px) {
                .method-grid { grid-template-columns: 1fr !important; gap: 10px !important; }
                .sheet-connect-grid { grid-template-columns: 1fr !important; }
                .source-row {
                  flex-wrap: wrap !important;
                  row-gap: 6px !important;
                }
                .source-row > div:nth-child(3) {
                  font-size: 10px !important;
                  order: 4;
                  flex-basis: 100%;
                }
              }
            `}</style>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button
              onClick={syncAll}
              data-testid="sync-all-now"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 7,
                padding: "8px 14px",
                minHeight: 34,
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: "0.04em",
                background: "#22382C",
                color: "#F4EFE4",
                border: "1px solid #22382C",
                borderRadius: 7,
                cursor: "pointer",
                transition: "background .15s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#2C4A3B")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "#22382C")}
            >
              <RefreshCw size={13} /> Sync all now
            </button>
            <button
              onClick={() => setAddLeadOpen(true)}
              data-testid="import-add-lead"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 7,
                padding: "8px 14px",
                minHeight: 34,
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: "0.04em",
                background: "#FFFFFF",
                color: "#2A2722",
                border: "1px solid #E4DCCD",
                borderRadius: 7,
                cursor: "pointer",
                transition: "border-color .15s, color .15s, background .15s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#22382C";
                e.currentTarget.style.color = "#22382C";
                e.currentTarget.style.background = "#FAF6EC";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#E4DCCD";
                e.currentTarget.style.color = "#2A2722";
                e.currentTarget.style.background = "#FFFFFF";
              }}
            >
              <Plus size={13} /> Add lead
            </button>
          </div>
        </div>

        {/* Google Sheet connect / revoked / EDIT card
            Module 6J — also renders when the user explicitly asks to edit
            an already-connected source (editingSheet=true). */}
        {sheetCfg && ((sheetCfg.status !== "connected") || editingSheet) && (
          <SheetConnectCard
            cfg={sheetCfg}
            serviceAccount={serviceAccount}
            onSaved={() => { setEditingSheet(false); refresh(); }}
            onClose={editingSheet ? () => setEditingSheet(false) : null}
            isEditing={editingSheet}
          />
        )}

        {/* Import Methods section */}
        <div style={{ marginBottom: 36 }}>
          <SectionEyebrow>Import Methods</SectionEyebrow>
          <div
            className="method-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 12,
            }}
          >
            <div style={{ position: "relative" }}>
              <MethodCard
                testId="method-google-sheet"
                eyebrow="METHOD"
                title="Google Sheet"
                subtitle={sheetConnected ? `Connected · …${(sheetCfg.sheet_id || "").slice(-6)}` : "Auto-sync from a shared sheet"}
                accentTop
                configured={sheetConnected}
                mobileIcon={SheetIcon}
                mobileTileBg="#DCE7DC"
                mobileTileInk="#3E5C48"
              />
              {/* Module 6J — Edit affordance on the connected Method card.
                  Absolutely-positioned pencil so we don't disturb the
                  MethodCard's own layout / testids. */}
              {sheetConnected && !editingSheet && (
                <button
                  onClick={() => setEditingSheet(true)}
                  data-testid="sheet-method-edit"
                  title="Edit Google Sheet source"
                  style={{
                    position: "absolute",
                    top: 10, right: 10,
                    padding: 6, minWidth: 30, minHeight: 30,
                    background: "#FFFFFF",
                    border: "1px solid #E4DCCD",
                    borderRadius: 6,
                    cursor: "pointer",
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    color: "#22382C",
                    transition: "background .15s, border-color .15s",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "#FAF6EC"; e.currentTarget.style.borderColor = "#22382C"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "#FFFFFF"; e.currentTarget.style.borderColor = "#E4DCCD"; }}
                >
                  <Pencil size={13} strokeWidth={2.2} />
                </button>
              )}
            </div>
            <MethodCard
              testId="method-csv-url"
              eyebrow="METHOD"
              title="CSV URL"
              subtitle="Pull from a hosted CSV link"
              cta="Add URL"
              onCta={() => setCsvUrlOpen(true)}
              mobileIcon={LinkIcon}
              mobileTileBg="#DCE4EC"
              mobileTileInk="#3B5468"
            />
            <MethodCard
              testId="method-csv-upload"
              eyebrow="METHOD"
              title="CSV upload"
              subtitle="Upload a file from your device"
              cta="Choose file"
              onCta={() => fileRef.current?.click()}
              mobileIcon={Upload}
              mobileTileBg="#F0E1D5"
              mobileTileInk="#95643C"
            />
            <MethodCard
              testId="method-manual"
              eyebrow="METHOD"
              title="Manual entry"
              subtitle="Add a lead by hand"
              cta="Add lead"
              onCta={() => setAddLeadOpen(true)}
              accentTop
              mobileIcon={Pencil}
              mobileTileBg="#E4DEEC"
              mobileTileInk="#5A4E77"
            />
            <MethodCard
              testId="method-wedmegood"
              eyebrow="METHOD"
              title="WedMeGood Gmail"
              subtitle="Auto-parse enquiry emails"
              comingSoon
              mobileIcon={Mail}
              mobileTileBg="#EFE8DA"
              mobileTileInk="#8A7346"
            />
            {!isMobile && <PlaceholderCard />}
            <input
              ref={fileRef}
              type="file"
              accept=".csv"
              onChange={onUpload}
              className="hidden"
              data-testid="csv-upload-input"
            />
          </div>
        </div>

        {/* Configured sources */}
        <div style={{ marginBottom: 32 }} data-testid="configured-sources">
          <SectionEyebrow>Configured Sources</SectionEyebrow>
          <div
            style={{
              background: "#FFFFFF",
              border: "1px solid #E9E2D6",
              borderRadius: 10,
              boxShadow: "0 2px 6px rgba(60,46,20,.04)",
              overflow: "hidden",
            }}
          >
            {sources.length === 0 ? (
              <div
                style={{
                  padding: "16px 20px",
                  fontSize: 13,
                  fontStyle: "italic",
                  color: "#B0A891",
                }}
              >
                No sources yet.
              </div>
            ) : sources.map((s, i) => (
              <div
                key={s.id}
                data-testid={`source-row-${s.id}`}
                className="source-row"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  padding: "11px 16px",
                  borderTop: i > 0 ? "1px solid #F2EDE2" : "none",
                  transition: "background .15s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#FAF6EC")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                <div style={{ width: 3, height: 28, background: edgeColor(s.type), borderRadius: 2 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#22382C" }}>{s.name}</div>
                  <div
                    style={{
                      fontSize: 11,
                      color: "#76705F",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {s.url || (s.sheet_id ? `Sheet ID: ${s.sheet_id}` : "")}
                  </div>
                </div>
                <div style={{ fontSize: 11, color: "#A39A8B" }}>
                  {s.last_synced_at ? `Synced ${new Date(s.last_synced_at).toLocaleString("en-IN")}` : "Never synced"}
                </div>
                <button
                  onClick={() => syncOne(s.id)}
                  data-testid={`sync-${s.id}`}
                  style={{
                    padding: "5px 10px",
                    minHeight: 26,
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: "0.04em",
                    border: "1px solid #E4DCCD",
                    background: "#FFFFFF",
                    color: "#2A2722",
                    borderRadius: 6,
                    cursor: "pointer",
                    transition: "border-color .15s, color .15s, background .15s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "#22382C";
                    e.currentTarget.style.color = "#22382C";
                    e.currentTarget.style.background = "#FAF6EC";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "#E4DCCD";
                    e.currentTarget.style.color = "#2A2722";
                    e.currentTarget.style.background = "#FFFFFF";
                  }}
                >
                  Sync
                </button>
                {/* Module 6J — Edit action for Google Sheet source (virtual).
                    Bypasses the virtual-only-hides-Delete gate below; opens
                    the same edit form the Method-card pencil opens. */}
                {s.type === "google_sheet" && (
                  <button
                    onClick={() => setEditingSheet(true)}
                    data-testid={`edit-${s.id}`}
                    style={{
                      padding: "5px 10px", minHeight: 26, fontSize: 11,
                      fontWeight: 600, letterSpacing: "0.04em",
                      border: "1px solid #E4DCCD",
                      background: "#FFFFFF", color: "#2A2722",
                      borderRadius: 6, cursor: "pointer",
                      transition: "border-color .15s, color .15s, background .15s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = "#22382C";
                      e.currentTarget.style.color = "#22382C";
                      e.currentTarget.style.background = "#FAF6EC";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = "#E4DCCD";
                      e.currentTarget.style.color = "#2A2722";
                      e.currentTarget.style.background = "#FFFFFF";
                    }}
                  >
                    Edit
                  </button>
                )}
                {!s.virtual && (
                  <button
                    onClick={async () => { await api.delete(`/import/sources/${s.id}`); refresh(); }}
                    style={{
                      padding: "5px 8px",
                      minHeight: 26,
                      fontSize: 11,
                      fontWeight: 600,
                      letterSpacing: "0.04em",
                      color: "#A35C45",
                      background: "transparent",
                      border: "1px solid transparent",
                      borderRadius: 6,
                      cursor: "pointer",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#F3D0C6")}
                    onMouseLeave={(e) => (e.currentTarget.style.borderColor = "transparent")}
                  >
                    Delete
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Import issues */}
        <div style={{ marginBottom: 36 }} data-testid="import-issues-panel">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <div style={{ display: "inline-flex", alignItems: "baseline", gap: 8 }}>
              <SectionEyebrow inline>Import Issues</SectionEyebrow>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: "#A39A8B",
                  letterSpacing: "0.05em",
                }}
              >
                {issues.length}
              </span>
            </div>
            {issues.length > 0 && (
              <button
                onClick={async () => { await api.post("/import/issues/clear"); refresh(); }}
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: "0.04em",
                  color: "#B08D55",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  textDecoration: "underline",
                  padding: "4px 6px",
                  minHeight: 24,
                }}
              >
                Clear all
              </button>
            )}
          </div>
          <div
            style={{
              background: "#FFFFFF",
              border: "1px solid #E9E2D6",
              borderRadius: 10,
              boxShadow: "0 2px 6px rgba(60,46,20,.04)",
              overflow: "hidden",
            }}
          >
            {issues.length === 0 ? (
              <div style={{ padding: "16px 20px", fontSize: 13, fontStyle: "italic", color: "#B0A891" }}>
                No issues.
              </div>
            ) : issues.map((iss, idx) => (
              <div
                key={iss.id}
                data-testid={`issue-${iss.id}`}
                style={{
                  display: "flex",
                  gap: 12,
                  padding: "12px 16px",
                  borderTop: idx > 0 ? "1px solid #F2EDE2" : "none",
                }}
              >
                <AlertTriangle size={15} color="#A35C45" style={{ flexShrink: 0, marginTop: 2 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#22382C" }}>{iss.reason}</div>
                  <div style={{ fontSize: 11, color: "#76705F", marginTop: 2 }}>
                    {iss.source} · row {iss.row_no} · {new Date(iss.created_at).toLocaleString("en-IN")}
                  </div>
                  <pre
                    style={{
                      fontSize: 11,
                      marginTop: 6,
                      background: "#F7F3EC",
                      border: "1px solid #EDE5D8",
                      borderRadius: 5,
                      padding: 8,
                      maxHeight: 80,
                      overflowX: "auto",
                      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                    }}
                  >
                    {JSON.stringify(iss.raw).slice(0, 220)}
                  </pre>
                </div>
                <button
                  onClick={async () => { await api.delete(`/import/issues/${iss.id}`); refresh(); }}
                  title="Dismiss"
                  style={{
                    padding: 6,
                    minWidth: 26,
                    minHeight: 26,
                    color: "#76705F",
                    background: "transparent",
                    border: "1px solid transparent",
                    borderRadius: 6,
                    cursor: "pointer",
                    alignSelf: "flex-start",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "#E4DCCD";
                    e.currentTarget.style.color = "#22382C";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "transparent";
                    e.currentTarget.style.color = "#76705F";
                  }}
                >
                  <X size={13} />
                </button>
              </div>
            ))}
          </div>
        </div>

        <AddLeadModal open={addLeadOpen} onClose={() => setAddLeadOpen(false)} onCreated={(payload) => { refresh(); const id = payload?.lead_id || payload; if (id && typeof id === "string") toast(`Existing lead: ${id} — open Pipeline to view.`); }} />
        {csvUrlOpen && <AddCsvUrlModal onClose={() => setCsvUrlOpen(false)} onSaved={refresh} onPreview={(payload) => setPreviewModal(payload)} />}
        {previewModal && <PreviewModal payload={previewModal} onClose={() => setPreviewModal(null)} onDone={refresh} />}
      </div>
    </Layout>
  );
}

function edgeColor(type) {
  if (type === "google_sheet") return "#22382C";
  if (type === "csv_url") return "#5E7E58";
  if (type === "wedmegood") return "#B08D55";
  return "#A39A8B";
}

function SectionEyebrow({ children, inline }) {
  return (
    <div
      style={{
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.18em",
        textTransform: "uppercase",
        color: "#B08D55",
        marginBottom: inline ? 0 : 10,
        display: inline ? "inline-block" : "block",
      }}
    >
      {children}
    </div>
  );
}

function MethodCard({ eyebrow, title, subtitle, cta, onCta, comingSoon, configured, accentTop, testId, mobileIcon, mobileTileBg, mobileTileInk }) {
  const [hover, setHover] = useState(false);
  const isMobile = useIsMobile();

  if (isMobile) {
    // ============ MOBILE ROW VARIANT ============
    // Icon-left row with title + subtitle + chevron (or COMING SOON pill).
    const clickable = !comingSoon && (onCta || configured);
    const Icon = mobileIcon;
    return (
      <div
        data-testid={testId}
        onClick={clickable ? onCta : undefined}
        role={clickable ? "button" : undefined}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 13,
          background: comingSoon ? "#FBF8F1" : "#FFFDFA",
          border: `1px solid rgba(34,56,44,${comingSoon ? 0.08 : 0.10})`,
          borderRadius: 14,
          padding: "14px 15px",
          minHeight: 68,
          cursor: clickable ? "pointer" : "default",
          opacity: comingSoon ? 0.75 : 1,
        }}
      >
        {Icon && (
          <div
            aria-hidden="true"
            style={{
              width: 40,
              height: 40,
              flexShrink: 0,
              borderRadius: 11,
              background: mobileTileBg || "#EFE8DA",
              color: mobileTileInk || "#8A7346",
              display: "grid",
              placeItems: "center",
            }}
          >
            <Icon size={19} strokeWidth={1.8} />
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14.5, fontWeight: 600, color: comingSoon ? "rgba(34,56,44,0.6)" : "#22382C" }}>
            {title}
          </div>
          <div style={{ fontSize: 11.5, color: comingSoon ? "rgba(34,56,44,0.45)" : "rgba(34,56,44,0.5)" }}>
            {subtitle}
          </div>
        </div>
        {configured && !comingSoon && (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: "#3F6B51",
              background: "rgba(63,107,81,0.12)",
              padding: "4px 9px",
              borderRadius: 6,
              whiteSpace: "nowrap",
            }}
          >
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#3F6B51" }} />
            Configured
          </span>
        )}
        {comingSoon && (
          <span
            style={{
              fontSize: 9,
              fontWeight: 800,
              letterSpacing: "0.06em",
              color: "#95643C",
              background: "rgba(176,141,85,0.16)",
              padding: "4px 9px",
              borderRadius: 6,
              whiteSpace: "nowrap",
            }}
          >
            COMING SOON
          </span>
        )}
        {!configured && !comingSoon && (
          <ChevronRight size={19} color="rgba(34,56,44,0.28)" />
        )}
      </div>
    );
  }

  // ============ DESKTOP CARD (original) ============
  return (
    <div
      data-testid={testId}
      onMouseEnter={() => !comingSoon && setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: "relative",
        background: "#FFFFFF",
        border: "1px solid #E9E2D6",
        borderRadius: 10,
        padding: "14px 16px",
        opacity: comingSoon ? 0.6 : 1,
        cursor: comingSoon ? "default" : "auto",
        boxShadow: hover ? "0 5px 14px rgba(60,46,20,.10)" : "0 2px 6px rgba(60,46,20,.05)",
        transform: hover ? "translateY(-1px)" : "translateY(0)",
        transition: "box-shadow .15s ease, transform .15s ease",
        overflow: "hidden",
      }}
    >
      {accentTop && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            background: "#22382C",
          }}
        />
      )}
      <div
        style={{
          fontSize: 9,
          fontWeight: 700,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: "#B08D55",
          marginBottom: 6,
          marginTop: accentTop ? 3 : 0,
        }}
      >
        {eyebrow}
      </div>
      <div
        className="font-display"
        style={{ fontSize: 18, lineHeight: 1.15, color: "#22382C", marginBottom: 4 }}
      >
        {title}
      </div>
      <div style={{ fontSize: 12, color: "#76705F", marginBottom: 10, lineHeight: 1.4 }}>
        {subtitle}
      </div>
      {configured && (
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, minHeight: 24 }}>
          <span
            style={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              background: "#3F6B51",
              boxShadow: "0 0 0 3px rgba(63,107,81,.15)",
            }}
          />
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: "#3F6B51",
            }}
          >
            Configured
          </span>
        </div>
      )}
      {!configured && comingSoon && (
        <span
          style={{
            display: "inline-block",
            background: "#F4EDDF",
            color: "#9A7A3C",
            fontSize: 9,
            fontWeight: 700,
            letterSpacing: "0.10em",
            textTransform: "uppercase",
            padding: "3px 8px",
            borderRadius: 5,
            minHeight: 18,
          }}
        >
          Coming Soon
        </span>
      )}
      {!configured && !comingSoon && cta && (
        <button
          onClick={onCta}
          disabled={comingSoon}
          style={{
            padding: "4px 2px",
            minHeight: 24,
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: "0.02em",
            color: "#B08D55",
            background: "transparent",
            border: "none",
            cursor: "pointer",
            transition: "color .15s",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = "#9A7A3C")}
          onMouseLeave={(e) => (e.currentTarget.style.color = "#B08D55")}
        >
          {cta} <span aria-hidden style={{ marginLeft: 2 }}>→</span>
        </button>
      )}
    </div>
  );
}

function PlaceholderCard() {
  return (
    <div
      aria-hidden
      style={{
        background: "transparent",
        border: "1px dashed #E0D5BE",
        borderRadius: 10,
        padding: "14px 16px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        minHeight: 132,
        color: "#B0A891",
      }}
    >
      <div
        style={{
          width: 26,
          height: 26,
          borderRadius: "50%",
          border: "1px dashed #D7CCB1",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Plus size={13} />
      </div>
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          color: "#B0A891",
        }}
      >
        More sources soon
      </div>
    </div>
  );
}

function SheetConnectCard({ cfg, serviceAccount, onSaved, onClose, isEditing }) {
  const [sheetId, setSheetId] = useState(cfg.sheet_id || "");
  const [range, setRange] = useState(cfg.sheet_range || "'Sheet1'!A:U");
  const [testing, setTesting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [err, setErr] = useState(null);

  const isRevoked = cfg.status === "permission_denied";

  const test = async () => {
    setTesting(true); setErr(null);
    try {
      const { data } = await api.post("/settings/google-sheet/test", { sheet_id: sheetId, sheet_range: range });
      if (data.ok) {
        toast.success("Connected!");
        // Module 6J v2 — auto-trigger a sync of the Google Sheet source
        // right after the config is saved, so the user sees fresh leads
        // without a manual "Sync" click. Failures are swallowed with a
        // soft toast so the connect success isn't undone by a sync hiccup.
        try {
          const { data: syncRes } = await api.post("/import/sources/google_sheet_default/sync");
          const c = syncRes?.counts;
          if (c) toast.success(`Synced: +${c.inserted} · refreshed ${c.refreshed}`);
        } catch (syncErr) {
          const det = syncErr?.response?.data?.detail;
          toast(typeof det === "string" ? det : det?.error || "Saved. Sync failed — click Sync to retry.");
        }
        onSaved?.();
      }
      else {
        setErr(data.error || data.status);
        if (data.status === "permission_denied") toast.error("Sheet not shared with the service account.");
        else if (data.status === "not_found") toast.error("Sheet ID not found.");
        else if (data.status === "platform_unconfigured") toast.error("Platform service account not configured.");
        else toast.error(data.error || "Test failed");
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || "Test failed");
    } finally { setTesting(false); }
  };

  // Module 6J — Disconnect: PUT sheet_id:"" resets studio.google_sheet_status
  // to "unconfigured" (see backend/routers/settings.py:45-46). Same API as
  // the DevTools workaround, exposed as a proper button.
  const disconnect = async () => {
    setDisconnecting(true);
    try {
      await api.put("/settings/google-sheet", { sheet_id: "" });
      toast.success("Disconnected. You can now configure a different sheet.");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Disconnect failed");
    } finally { setDisconnecting(false); }
  };

  const copyEmail = () => {
    navigator.clipboard.writeText(serviceAccount || "");
    toast("Copied");
  };

  return (
    <div
      data-testid="sheet-connect-card"
      style={{
        position: "relative",
        background: "#FFFFFF",
        border: "1px solid #E9E2D6",
        borderTop: "3px solid #22382C",
        borderRadius: 10,
        padding: "18px 22px",
        marginBottom: 28,
        boxShadow: "0 2px 6px rgba(60,46,20,.05)",
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          color: "#B08D55",
          marginBottom: 6,
        }}
      >
        Google Sheet
      </div>
      <div
        className="font-display"
        style={{
          fontSize: 24,
          lineHeight: 1.15,
          color: isRevoked ? "#A35C45" : "#22382C",
          marginBottom: 12,
        }}
      >
        {isRevoked ? "Access was revoked"
          : isEditing ? "Edit your Google Sheet source"
          : "Connect your Google Sheet"}
      </div>

      {/* Module 6J — Close (×) button when this card is opened via the Edit
          affordance. Not shown on first-time connect so we don't offer
          "close" on a card the user has to complete. */}
      {onClose && (
        <button
          onClick={onClose}
          data-testid="sheet-edit-close"
          title="Close"
          style={{
            position: "absolute", top: 12, right: 12,
            padding: 6, minWidth: 30, minHeight: 30,
            background: "transparent", border: "1px solid transparent",
            borderRadius: 6, cursor: "pointer",
            color: "#76705F",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "#F4EFE4"; e.currentTarget.style.borderColor = "#E4DCCD"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = "transparent"; }}
        >
          <X size={14} strokeWidth={2.2} />
        </button>
      )}

      {!serviceAccount && (
        <div
          style={{
            background: "#FBF0EC",
            border: "1px solid #F3D0C6",
            borderRadius: 7,
            padding: "10px 14px",
            fontSize: 12,
            color: "#A35C45",
            marginBottom: 14,
            lineHeight: 1.5,
          }}
        >
          The platform service account isn&apos;t configured yet. Ask your admin to set{" "}
          <Code>GOOGLE_SERVICE_ACCOUNT_JSON</Code>.
        </div>
      )}

      <ol style={{ margin: 0, padding: 0, listStyle: "none", marginBottom: 16 }}>
        <Step n={1}>Open your Google Sheet → Share.</Step>
        <Step n={2}>
          Add this email as <span style={{ fontWeight: 600, color: "#22382C" }}>Viewer</span>:
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              marginLeft: 8,
              padding: "2px 8px",
              background: "#F2EBD9",
              border: "1px solid #E4DCCD",
              borderRadius: 5,
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              fontSize: 11,
              color: "#2A2722",
            }}
          >
            <span data-testid="service-account-email">{serviceAccount || "(not configured)"}</span>
            <button
              onClick={copyEmail}
              title="Copy"
              disabled={!serviceAccount}
              style={{
                padding: 2,
                minWidth: 24,
                minHeight: 24,
                background: "transparent",
                border: "none",
                color: "#76705F",
                cursor: serviceAccount ? "pointer" : "not-allowed",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Copy size={12} />
            </button>
          </span>
        </Step>
        <Step n={3}>
          Paste your sheet ID below (between <Code>/d/</Code> and <Code>/edit</Code> in the URL).
        </Step>
      </ol>

      <div
        className="sheet-connect-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0,1fr) 180px auto",
          gap: 10,
          alignItems: "end",
        }}
      >
        <Field label="Sheet ID">
          <input
            value={sheetId}
            onChange={(e) => setSheetId(e.target.value)}
            className="fld"
            data-testid="sheet-id-input"
          />
        </Field>
        <Field label="Range">
          <input
            value={range}
            onChange={(e) => setRange(e.target.value)}
            className="fld"
            data-testid="sheet-range-input"
          />
        </Field>
        <button
          onClick={test}
          disabled={testing || !sheetId}
          data-testid="sheet-test-button"
          style={{
            height: 36,
            padding: "0 14px",
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: "0.04em",
            background: testing || !sheetId ? "#9AAEA3" : "#22382C",
            color: "#F4EFE4",
            border: `1px solid ${testing || !sheetId ? "#9AAEA3" : "#22382C"}`,
            borderRadius: 7,
            cursor: testing || !sheetId ? "not-allowed" : "pointer",
            transition: "background .15s",
          }}
          onMouseEnter={(e) => {
            if (!(testing || !sheetId)) e.currentTarget.style.background = "#2C4A3B";
          }}
          onMouseLeave={(e) => {
            if (!(testing || !sheetId)) e.currentTarget.style.background = "#22382C";
          }}
        >
          {testing ? "Testing…" : isRevoked ? "Re-test" : "Test connection"}
        </button>
        {/* Module 6J — Disconnect only offered when editing an already-
            connected source. Uses same PUT endpoint with sheet_id="" so
            the studio drops back to 'unconfigured' status. */}
        {isEditing && (
          <button
            onClick={disconnect}
            disabled={disconnecting}
            data-testid="sheet-disconnect-button"
            style={{
              height: 36,
              padding: "0 14px",
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: "0.04em",
              background: "transparent",
              color: "#A35C45",
              border: "1px solid #E9D2C7",
              borderRadius: 7,
              cursor: disconnecting ? "not-allowed" : "pointer",
              opacity: disconnecting ? 0.6 : 1,
              marginLeft: "auto",
            }}
          >
            {disconnecting ? "Disconnecting…" : "Disconnect"}
          </button>
        )}
      </div>

      {err && (
        <div
          data-testid="sheet-error"
          style={{
            marginTop: 12,
            padding: "8px 12px",
            background: "#FBF0EC",
            border: "1px solid #F3D0C6",
            color: "#A35C45",
            fontSize: 12,
            borderRadius: 6,
          }}
        >
          {err}
        </div>
      )}

      <style>{`
        .fld{
          width:100%;
          height:36px;
          padding:0 10px;
          border:1px solid #E4DCCD;
          background:#FFFFFF;
          font-size:13px;
          color:#2A2722;
          outline:none;
          border-radius:7px;
          font-family:var(--ds-body-font);
          transition:border-color .15s;
        }
        .fld:focus{border-color:#22382C;}
      `}</style>
    </div>
  );
}

function Step({ n, children }) {
  return (
    <li style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 8 }}>
      <span
        style={{
          flexShrink: 0,
          fontSize: 11,
          fontWeight: 700,
          color: "#B08D55",
          lineHeight: 1.5,
          minWidth: 12,
        }}
      >
        {n}
      </span>
      <span style={{ fontSize: 13, color: "#2A2722", lineHeight: 1.5 }}>{children}</span>
    </li>
  );
}

function Code({ children }) {
  return (
    <code
      style={{
        background: "#F2EBD9",
        border: "1px solid #E4DCCD",
        borderRadius: 5,
        padding: "2px 6px",
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        fontSize: 11,
        color: "#2A2722",
      }}
    >
      {children}
    </code>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: "block" }}>
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          marginBottom: 4,
          color: "#A39A8B",
        }}
      >
        {label}
      </div>
      {children}
    </label>
  );
}

function AddCsvUrlModal({ onClose, onSaved, onPreview }) {
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!url.trim() || !name.trim()) return;
    setSaving(true);
    try {
      const { data: src } = await api.post("/import/sources", { name: name.trim(), url: url.trim(), type: "csv_url" });
      const { data: preview } = await api.post("/import/preview", { source_type: "source_id", source_id: src.id });
      onPreview({ ...preview, source_id: src.id, source_type: "source_id", url });
      onClose();
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add");
    } finally { setSaving(false); }
  };

  return (
    <div
      data-testid="csv-url-modal"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 50,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        background: "rgba(34,56,44,0.42)",
        backdropFilter: "blur(2px)",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#FFFFFF",
          maxWidth: 460,
          width: "100%",
          border: "1px solid #E9E2D6",
          borderTop: "3px solid #22382C",
          borderRadius: 10,
          boxShadow: "0 18px 40px rgba(34,56,44,.18)",
          overflow: "hidden",
        }}
      >
        <div style={{ padding: 24 }}>
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: "#B08D55",
              marginBottom: 6,
            }}
          >
            Method A
          </div>
          <h2
            className="font-display"
            style={{ fontSize: 22, color: "#22382C", marginBottom: 8, lineHeight: 1.15 }}
          >
            Add CSV URL
          </h2>
          <p style={{ fontSize: 12, color: "#76705F", marginBottom: 16, lineHeight: 1.5 }}>
            In Google Sheets: File → Share → Publish to web → comma-separated values (.csv) → copy link.
          </p>
          <Field label="Name">
            <input value={name} onChange={(e) => setName(e.target.value)} className="fld" data-testid="csv-url-name" />
          </Field>
          <div style={{ height: 12 }} />
          <Field label="CSV URL">
            <input value={url} onChange={(e) => setUrl(e.target.value)} className="fld" data-testid="csv-url-url" />
          </Field>
        </div>
        <div
          style={{
            padding: "14px 24px",
            borderTop: "1px solid #EDE5D8",
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
            background: "#FAF6EC",
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: "8px 14px",
              minHeight: 32,
              fontSize: 12,
              fontWeight: 600,
              color: "#76705F",
              background: "transparent",
              border: "1px solid transparent",
              borderRadius: 6,
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="csv-url-save"
            style={{
              padding: "8px 16px",
              minHeight: 32,
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: "0.04em",
              color: "#F4EFE4",
              background: saving ? "#9AAEA3" : "#22382C",
              border: `1px solid ${saving ? "#9AAEA3" : "#22382C"}`,
              borderRadius: 7,
              cursor: saving ? "not-allowed" : "pointer",
            }}
          >
            {saving ? "Saving…" : "Save & preview"}
          </button>
        </div>
        <style>{`
          .fld{
            width:100%;
            height:36px;
            padding:0 10px;
            border:1px solid #E4DCCD;
            background:#FFFFFF;
            font-size:13px;
            color:#2A2722;
            outline:none;
            border-radius:7px;
            font-family:var(--ds-body-font);
            transition:border-color .15s;
          }
          .fld:focus{border-color:#22382C;}
        `}</style>
      </div>
    </div>
  );
}

function PreviewModal({ payload, onClose, onDone }) {
  const [remember, setRemember] = useState(true);
  const [importing, setImporting] = useState(false);
  const [mapping, setMapping] = useState(payload.mapping || {});
  const FIELD_OPTIONS = [
    "__ignore__", "__form_answer__",
    "full_name", "phone", "email", "platform", "campaign_name", "campaign_id",
    "adset_name", "adset_id", "ad_name", "ad_id", "form_name", "form_id", "lead_id",
    "wedding_date", "venue_status", "wedding_lineup", "office_visit_interest",
    "wedding_vibe", "budget_range", "is_organic", "inbox_url", "stage", "category",
    "source_status", "created_at",
  ];

  const allHeaders = [...new Set([
    ...Object.keys(payload.mapping || {}),
    ...(payload.unknown_headers || []),
  ])];

  useEffect(() => {
    const next = { ...mapping };
    let changed = false;
    for (const h of payload.unknown_headers || []) {
      if (!(h in next)) { next[h] = "__ignore__"; changed = true; }
    }
    if (changed) setMapping(next);
  }, []);

  const confirm = async () => {
    setImporting(true);
    try {
      const body = {
        source_type: payload.source_type, source_id: payload.source_id,
        url: payload.url, csv_text: payload.csv_text,
        overrides: mapping, remember,
      };
      const { data } = await api.post("/import/confirm", body);
      const c = data.counts;
      toast.success(`+${c.inserted} new · ${c.refreshed} refreshed · ${c.quarantined} quarantined`);
      onClose(); onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally { setImporting(false); }
  };

  return (
    <div
      data-testid="preview-modal"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 50,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        background: "rgba(34,56,44,0.42)",
        backdropFilter: "blur(2px)",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#FFFFFF",
          maxWidth: 760,
          width: "100%",
          maxHeight: "85vh",
          overflowY: "auto",
          border: "1px solid #E9E2D6",
          borderTop: "3px solid #22382C",
          borderRadius: 10,
          boxShadow: "0 18px 40px rgba(34,56,44,.18)",
        }}
      >
        <div style={{ padding: 22 }}>
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: "#B08D55",
              marginBottom: 6,
            }}
          >
            Preview {payload.filename ? `· ${payload.filename}` : ""}
          </div>
          <h2
            className="font-display"
            style={{ fontSize: 22, color: "#22382C", marginBottom: 4, lineHeight: 1.15 }}
          >
            {payload.row_count} rows · {Object.keys(mapping).filter((k) => mapping[k] && !mapping[k].startsWith("__")).length} mapped
          </h2>
          {payload.mapping_known && (
            <p style={{ fontSize: 11, color: "#76705F", marginBottom: 12 }}>
              Mapping remembered from a prior import — review and edit if needed.
            </p>
          )}
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: "#B08D55",
              marginBottom: 8,
              marginTop: 14,
            }}
          >
            Column Mapping
          </div>
          <table style={{ width: "100%", fontSize: 12, marginBottom: 14, borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#FAF6EC" }}>
                <th
                  style={{
                    padding: "8px 10px",
                    textAlign: "left",
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.13em",
                    textTransform: "uppercase",
                    color: "#A39A8B",
                  }}
                >
                  CSV header
                </th>
                <th
                  style={{
                    padding: "8px 10px",
                    textAlign: "left",
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.13em",
                    textTransform: "uppercase",
                    color: "#A39A8B",
                  }}
                >
                  Maps to
                </th>
              </tr>
            </thead>
            <tbody>
              {allHeaders.map((h) => {
                const isUnknown = (payload.unknown_headers || []).includes(h);
                return (
                  <tr key={h} data-testid={`mapping-row-${h}`} style={{ borderTop: "1px solid #F2EDE2" }}>
                    <td style={{ padding: "8px 10px", verticalAlign: "middle", color: "#2A2722" }}>
                      <span>{h}</span>
                      {isUnknown && (
                        <span
                          style={{
                            marginLeft: 8,
                            fontSize: 9,
                            fontWeight: 700,
                            letterSpacing: "0.18em",
                            textTransform: "uppercase",
                            color: "#A35C45",
                          }}
                        >
                          Unknown
                        </span>
                      )}
                    </td>
                    <td style={{ padding: "8px 10px" }}>
                      <select
                        value={mapping[h] || "__ignore__"}
                        onChange={(e) => setMapping((m) => ({ ...m, [h]: e.target.value }))}
                        className="al-input2"
                        data-testid={`mapping-select-${h}`}
                      >
                        {FIELD_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: "#B08D55",
              marginBottom: 8,
            }}
          >
            Sample rows (first 5)
          </div>
          <div
            style={{
              fontSize: 11,
              background: "#F7F3EC",
              border: "1px solid #EDE5D8",
              borderRadius: 6,
              padding: 10,
              maxHeight: 160,
              overflowY: "auto",
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              color: "#2A2722",
            }}
          >
            {(payload.samples || []).slice(0, 5).map((s, i) => (
              <div key={i} style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", marginBottom: 3 }}>
                {JSON.stringify(s)}
              </div>
            ))}
          </div>
        </div>
        <div
          style={{
            padding: "14px 22px",
            borderTop: "1px solid #EDE5D8",
            background: "#FAF6EC",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            position: "sticky",
            bottom: 0,
          }}
        >
          <label
            style={{
              fontSize: 11,
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              color: "#2A2722",
              minHeight: 24,
            }}
          >
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              data-testid="preview-remember"
            />
            Remember this mapping
          </label>
          <div style={{ display: "flex", gap: 10 }}>
            <button
              onClick={onClose}
              style={{
                padding: "8px 14px",
                minHeight: 32,
                fontSize: 12,
                fontWeight: 600,
                color: "#76705F",
                background: "transparent",
                border: "1px solid transparent",
                borderRadius: 6,
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              onClick={confirm}
              disabled={importing}
              data-testid="preview-confirm"
              style={{
                padding: "8px 16px",
                minHeight: 32,
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: "0.04em",
                color: "#F4EFE4",
                background: importing ? "#9AAEA3" : "#22382C",
                border: `1px solid ${importing ? "#9AAEA3" : "#22382C"}`,
                borderRadius: 7,
                cursor: importing ? "not-allowed" : "pointer",
              }}
            >
              {importing ? "Importing…" : "Import"}
            </button>
          </div>
        </div>
        <style>{`
          .al-input2{
            width:100%;
            padding:6px 8px;
            border:1px solid #E4DCCD;
            background:#FFFFFF;
            font-size:12px;
            color:#2A2722;
            outline:none;
            border-radius:6px;
            font-family:var(--ds-body-font);
          }
          .al-input2:focus{border-color:#22382C;}
        `}</style>
      </div>
    </div>
  );
}
